import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { injectable } from '@servicetitan/react-ioc';
import { PathLike } from 'fs';
import { stringify } from 'qs';

import { normalizeUrlWithParts } from '../utils/url';

export type Response<T> = Promise<AxiosResponse<T>>;

@injectable()
export class HttpClient {
    api: AxiosInstance;

    private readonly apiVersion = 1;

    private xhrInterceptors = new Map<'request' | 'response', ((xhr: XMLHttpRequest) => void)[]>([
        ['request', []],
        ['response', []],
    ]);

    constructor() {
        this.api = axios.create({
            baseURL: this.apiUrl,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            paramsSerializer: (params: PathLike) =>
                stringify(params, { indices: false, arrayFormat: 'repeat' }),
        });

        this.addXHRRequestInterceptor((xhr: XMLHttpRequest) => {
            if (typeof xhr._requestParams[1] === 'string') {
                const url = new URL(xhr._requestParams[1], window.location.origin);
                if (url.origin.includes('.st.dev') || url.origin.includes('.servicetitan.com')) {
                    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                }
            }
        });
        this.addXHRResponseInterceptor((xhr: XMLHttpRequest) => {
            if (xhr.status === 401) {
                window.location.href = `/home?redirectUrl=${encodeURIComponent(document.URL)}`;
            }
        });
        this.addXHRInterceptors();
    }

    get apiUrl(): string {
        return normalizeUrlWithParts(['/', 'api', `v${this.apiVersion}`]);
    }

    addRequestInterceptor = (
        interceptor: (value: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>
    ) => {
        this.api.interceptors.request.use(interceptor);
    };

    addResponseFulfilledInterceptor = (
        interceptor: (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>
    ) => {
        this.api.interceptors.response.use(interceptor);
    };

    addResponseRejectedInterceptor = (interceptor: (error: any) => any | undefined) => {
        this.api.interceptors.response.use(undefined, interceptor);
    };

    get<T>(url: string, config?: AxiosRequestConfig): Response<T> {
        return this.api.get(url, config);
    }

    post<T, B>(url: string, data?: B | string, config?: AxiosRequestConfig): Response<T> {
        return this.api.post(url, data, config);
    }

    put<T, B>(url: string, data: B, config?: AxiosRequestConfig): Response<T> {
        return this.api.put(url, data, config);
    }

    patch<T, B>(url: string, data: B, config?: AxiosRequestConfig): Response<T> {
        return this.api.patch(url, data, config);
    }

    delete(url: string, id: string, config?: AxiosRequestConfig) {
        return this.api.delete(`${url}/${id}`, config);
    }

    private addXHRRequestInterceptor(interceptor: (xhr: XMLHttpRequest) => void) {
        this.xhrInterceptors.get('request')!.push(interceptor);
    }

    private addXHRResponseInterceptor(interceptor: (xhr: XMLHttpRequest) => void) {
        this.xhrInterceptors.get('response')!.push(interceptor);
    }

    private addXHRInterceptors() {
        const responseInterceptors = this.xhrInterceptors.get('response')!;
        const requestInterceptors = this.xhrInterceptors.get('request')!;
        const oldSend = XMLHttpRequest.prototype.send;
        const oldOpen = XMLHttpRequest.prototype.open;

        XMLHttpRequest.prototype.open = function (
            ...args:
                | [
                      method: string,
                      url: string | URL,
                      async: boolean,
                      username?: string | null,
                      password?: string | null
                  ]
                | [method: string, url: string | URL]
        ) {
            this._requestParams = args;
            oldOpen.apply(this, args);
        };

        XMLHttpRequest.prototype.send = function (...args) {
            // eslint-disable-next-line prefer-const
            for (let interceptor of requestInterceptors) {
                interceptor(this);
            }
            if (responseInterceptors.length) {
                this.addEventListener('load', function () {
                    // eslint-disable-next-line prefer-const
                    for (let interceptor of responseInterceptors) {
                        interceptor(this);
                    }
                });
            }
            oldSend.apply(this, args);
        };
    }
}
